import React, { useEffect, useRef, useState } from "react";
import { User, Users, ArrowRight, BookOpen, Scale, FileCheck, Smartphone, Monitor } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Card version of the case flow, ported from the supplied Tailwind/React
// prototype. Two things had to change to run inside this app: the project has
// no Tailwind, so the utilities are ported into the scoped <style> block below
// (namespaced under #cfc), and the icons come from lucide-react — already a
// dependency — rather than the CDN <script> the prototype used.
//
// The staggered entrance keeps the prototype's delays but waits for the section
// to scroll into view, since it sits at the bottom of the homepage.
//
// Renders unconditionally on all environments (graduated from the
// "case-flow-cards" staging flag).
// ─────────────────────────────────────────────────────────────────────────────

const css = `
#cfc {
  --ink:#1f2937;
  --muted:#6b7280;
  --line:#d1d5db;
  --blue:#3b82f6;
  --blue-600:#2563eb;
  --blue-400:#60a5fa;
  --blue-200:#bfdbfe;
  --blue-100:#dbeafe;
  --blue-50:#eff6ff;
  --purple-600:#9333ea;
  --purple-200:#e9d5ff;
  --purple-100:#f3e8ff;
  --purple-50:#faf5ff;
  --green-600:#16a34a;
  --green-300:#86efac;
  --green-100:#dcfce7;
  --green-50:#f0fdf4;
  --amber-900:#78350f;
  --amber-800:#92400e;
  --amber-600:#d97706;
  --amber-300:#fcd34d;
  --amber-100:#fef3c7;
  --paper:#F5F5F2;
  --hair:rgba(17,18,20,0.09);
  --sans:'Plus Jakarta Sans',sans-serif;
  --mono:'DM Mono',monospace;
  border-top:1px solid var(--hair);
  padding:52px 0 64px;
  font-family:var(--sans);
  color:var(--ink);
}
#cfc .cfc-col{max-width:600px;margin:0 auto;padding:0 28px}
#cfc .cfc-wide{max-width:1160px;margin:0 auto;padding:0 28px}
@media(max-width:520px){#cfc{padding:40px 0 48px}#cfc .cfc-col,#cfc .cfc-wide{padding:0 18px}}

#cfc .cfc-kicker{
  font-family:var(--mono);font-size:10px;letter-spacing:0.15em;
  text-transform:uppercase;color:#1A5EC4;display:block;margin-bottom:14px;
}
#cfc h2{font-size:clamp(20px,3vw,24px);font-weight:800;letter-spacing:-0.025em;line-height:1.18;margin:0 0 10px}
#cfc .cfc-intro{font-size:14px;line-height:1.6;color:#3A3C40;margin:0 0 28px}

/* ── stage ─────────────────────────────────────────────────────────────── */
#cfc .cfc-stage{position:relative;padding-bottom:62px}
#cfc .cfc-row{
  display:flex;flex-direction:row;align-items:center;justify-content:space-between;
  gap:14px;position:relative;z-index:1;
}
#cfc .cfc-inputs{display:flex;flex-direction:column;gap:12px;width:190px;flex:0 0 auto}

/* ── cards ─────────────────────────────────────────────────────────────── */
#cfc .cfc-card{
  background:#fff;border-radius:12px;border:2px solid var(--blue-200);
  box-shadow:0 4px 6px -1px rgba(0,0,0,0.08),0 2px 4px -2px rgba(0,0,0,0.06);
  padding:12px;display:flex;align-items:center;gap:12px;
}
#cfc .cfc-card--them{border-color:var(--purple-200)}
#cfc .cfc-card--wit{border-style:dashed;border-color:var(--green-300);box-shadow:0 1px 2px rgba(0,0,0,0.05)}
#cfc .cfc-bubble{
  background:var(--blue-100);color:var(--blue-600);
  padding:8px;border-radius:999px;display:flex;flex:0 0 auto;
}
#cfc .cfc-card--them .cfc-bubble{background:var(--purple-100);color:var(--purple-600)}
#cfc .cfc-card--wit .cfc-bubble{background:var(--green-100);color:var(--green-600)}
#cfc .cfc-card h3{font-size:13px;font-weight:700;color:var(--ink);margin:0}
#cfc .cfc-card p{font-size:11.5px;color:var(--muted);margin:2px 0 0}

/* ── arrows ────────────────────────────────────────────────────────────── */
#cfc .cfc-arrow{display:flex;flex-direction:column;align-items:center;flex:0 0 auto;color:var(--muted)}
#cfc .cfc-arrow .cfc-tag{font-size:11px;font-weight:700;letter-spacing:0.14em;margin-bottom:4px;white-space:nowrap}
#cfc .cfc-arrow--decides .cfc-tag{font-size:13px;color:var(--ink)}
#cfc .cfc-arrow svg{color:var(--blue-400)}
#cfc .cfc-arrow--decides svg{color:var(--blue-600)}

/* ── record + panel ────────────────────────────────────────────────────── */
#cfc .cfc-box{
  width:190px;flex:0 0 auto;background:#fff;border:2px solid var(--ink);border-radius:16px;
  padding:16px;box-shadow:0 10px 15px -3px rgba(0,0,0,0.08),0 4px 6px -4px rgba(0,0,0,0.06);
}
#cfc .cfc-box--panel{background:var(--blue-50)}
#cfc .cfc-box h2{font-size:15px;font-weight:700;text-align:center;margin:0 0 14px}
#cfc .cfc-slots{display:flex;flex-direction:column;gap:8px}
#cfc .cfc-slot{
  border-radius:4px;padding:8px;font-size:12.5px;font-weight:500;text-align:center;
  background:var(--blue-50);border:1px solid var(--blue-200);
}
#cfc .cfc-slot--them{background:var(--purple-50);border-color:var(--purple-200)}
#cfc .cfc-slot--wit{background:var(--green-50);border-color:var(--green-300);border-style:dashed}
#cfc .cfc-note{text-align:center;font-size:11.5px;color:var(--muted);margin:14px 0 0;line-height:1.35}
#cfc .cfc-seats{display:flex;flex-direction:column;gap:10px}
#cfc .cfc-seat{
  background:#fff;border:1px solid var(--line);border-radius:8px;padding:8px;
  font-size:12.5px;font-weight:500;display:flex;align-items:center;justify-content:center;gap:8px;
}
#cfc .cfc-dot{width:15px;height:15px;border-radius:999px;flex:0 0 auto}
#cfc .cfc-dot--1{background:linear-gradient(to right,#fb923c,#ef4444)}
#cfc .cfc-dot--2{background:linear-gradient(to right,#60a5fa,#6366f1)}
#cfc .cfc-dot--3{background:linear-gradient(to right,#2dd4bf,#22c55e)}
#cfc .cfc-judge{margin-top:20px;display:flex;flex-direction:column;align-items:center;color:#4b5563}
#cfc .cfc-judge svg{color:#b45309;margin-bottom:6px}
#cfc .cfc-judge span{font-size:10px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;text-align:center}

/* ── gate ──────────────────────────────────────────────────────────────── */
#cfc .cfc-gate{display:flex;flex-direction:column;align-items:center;flex:0 0 auto}
#cfc .cfc-gate .cfc-tag{font-size:11px;font-weight:700;color:var(--muted);letter-spacing:0.14em;white-space:nowrap}
#cfc .cfc-gate-box{
  background:#fff;border:2px solid var(--ink);border-radius:8px;padding:8px;
  margin:8px 0;box-shadow:0 1px 2px rgba(0,0,0,0.05);display:flex;
}
#cfc .cfc-gate-box svg{color:var(--blue-600)}
#cfc .cfc-gate .cfc-gate-arrow{color:var(--ink);margin-top:4px}

/* ── resolution ────────────────────────────────────────────────────────── */
#cfc .cfc-resolution{
  width:190px;flex:0 0 auto;background:var(--amber-100);border:2px solid var(--amber-300);
  border-radius:12px;padding:20px 16px 16px;
  box-shadow:0 10px 15px -3px rgba(0,0,0,0.08),0 4px 6px -4px rgba(0,0,0,0.06);
}
#cfc .cfc-resolution h2{font-size:15px;font-weight:700;color:var(--amber-900);text-align:center;margin:0 0 8px}
#cfc .cfc-resolution p{font-size:11.5px;color:var(--amber-800);text-align:center;margin:0;line-height:1.45}
#cfc .cfc-seal{display:flex;justify-content:center;margin-top:14px}
#cfc .cfc-seal svg{color:var(--amber-600)}

/* ── the return loop ───────────────────────────────────────────────────── */
/* Overlays the whole stage. It must not animate with a transform: that would
   make it the containing block for its own absolutely positioned lines. */
#cfc .cfc-loop-wrap{position:absolute;inset:0;pointer-events:none;opacity:0}
#cfc .cfc-stage.playing .cfc-loop-wrap{animation:cfc-fade 0.8s ease-out forwards}
@keyframes cfc-fade{to{opacity:1}}
#cfc .cfc-loop-h,#cfc .cfc-loop-l,#cfc .cfc-loop-r{position:absolute;background:var(--blue);border-radius:2px}
#cfc .cfc-loop-h{left:8px;right:95px;bottom:24px;height:2px}
#cfc .cfc-loop-l{left:8px;top:38px;bottom:24px;width:2px}
#cfc .cfc-loop-r{right:95px;bottom:24px;height:48px;width:2px}
#cfc .cfc-loop-bracket{display:none}
#cfc .cfc-loop-label{
  position:absolute;left:50%;bottom:24px;transform:translate(-50%,50%);
  background:var(--paper);padding:0 14px;display:flex;align-items:center;gap:8px;
  font-size:11px;font-weight:700;color:var(--muted);letter-spacing:0.14em;
  text-transform:uppercase;white-space:nowrap;
}
#cfc .cfc-devices{
  position:absolute;right:20px;bottom:24px;transform:translateY(50%);
  background:var(--paper);padding:0 8px;display:flex;gap:8px;color:#475569;
}

/* ── entrance, staggered like the prototype ────────────────────────────── */
#cfc .cfc-fx{opacity:0}
#cfc .cfc-stage.playing .cfc-fx{animation:cfc-flow 0.8s ease-out forwards}
@keyframes cfc-flow{
  0%{opacity:0;transform:translateX(-20px)}
  100%{opacity:1;transform:translateX(0)}
}

/* ── mobile: the row becomes a column and the loop becomes a bracket ────── */
@media(max-width:768px){
  #cfc .cfc-row{flex-direction:column;align-items:stretch;gap:12px;padding-left:26px}
  #cfc .cfc-inputs,#cfc .cfc-box,#cfc .cfc-resolution{width:auto}
  #cfc .cfc-arrow,#cfc .cfc-gate{flex-direction:row;gap:10px;justify-content:center}
  #cfc .cfc-arrow .cfc-tag,#cfc .cfc-gate .cfc-tag{margin-bottom:0}
  #cfc .cfc-arrow svg,#cfc .cfc-gate .cfc-gate-arrow{transform:rotate(90deg)}
  #cfc .cfc-gate-box{margin:0}
  #cfc .cfc-stage{padding-bottom:44px}
  #cfc .cfc-loop-h,#cfc .cfc-loop-l,#cfc .cfc-loop-r,#cfc .cfc-devices{display:none}
  /* the loop becomes a bracket running up the left of the column */
  #cfc .cfc-loop-bracket{
    display:block;position:absolute;left:2px;top:34px;bottom:100px;width:44%;
    border:2px solid var(--blue);border-right:none;border-radius:10px 0 0 10px;
  }
  #cfc .cfc-loop-label{
    left:50%;bottom:4px;transform:translateX(-50%);background:none;padding:0;
    justify-content:center;white-space:normal;text-align:center;width:min(100%,320px);
  }
  #cfc .cfc-stage.playing .cfc-fx{animation-name:cfc-flow-v}
  @keyframes cfc-flow-v{
    0%{opacity:0;transform:translateY(-14px)}
    100%{opacity:1;transform:translateY(0)}
  }
}

@media (prefers-reduced-motion:reduce){
  #cfc .cfc-fx{opacity:1}
  #cfc .cfc-stage.playing .cfc-fx{animation:none}
}
`;

const D = (s) => ({ animationDelay: `${s}s` });

export default function CaseFlowCards() {
  const stageRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPlaying(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section id="cfc">
      <style>{css}</style>

      <div className="cfc-col">
        <span className="cfc-kicker">The flow, as cards</span>
        <h2>Three inputs, one record, one decision.</h2>
        <p className="cfc-intro">
          The same journey a case takes, laid out step by step — who puts what in, what the panel
          reads, and where the resolution lands.
        </p>
      </div>

      <div className="cfc-wide">
        <div className={`cfc-stage${playing ? " playing" : ""}`} ref={stageRef}>
          <div className="cfc-row">

            {/* Inputs */}
            <div className="cfc-inputs">
              <div className="cfc-card cfc-fx" style={D(0.2)}>
                <span className="cfc-bubble"><User size={18} /></span>
                <div>
                  <h3>You</h3>
                  <p>account + evidence</p>
                </div>
              </div>
              <div className="cfc-card cfc-card--them cfc-fx" style={D(0.4)}>
                <span className="cfc-bubble"><User size={18} /></span>
                <div>
                  <h3>The other side</h3>
                  <p>account + evidence</p>
                </div>
              </div>
              <div className="cfc-card cfc-card--wit cfc-fx" style={D(0.6)}>
                <span className="cfc-bubble"><Users size={18} /></span>
                <div>
                  <h3>Witnesses</h3>
                  <p>optional, up to two</p>
                </div>
              </div>
            </div>

            <div className="cfc-arrow cfc-fx" style={D(0.8)}>
              <span className="cfc-tag">FILES</span>
              <ArrowRight size={22} />
            </div>

            {/* One case record */}
            <div className="cfc-box cfc-fx" style={D(1.2)}>
              <h2>One case record</h2>
              <div className="cfc-slots">
                <div className="cfc-slot">your account</div>
                <div className="cfc-slot cfc-slot--them">their account</div>
                <div className="cfc-slot cfc-slot--wit">witness account</div>
              </div>
            </div>

            {/* Gate */}
            <div className="cfc-gate cfc-fx" style={D(1.6)}>
              <span className="cfc-tag">BOTH SIDES IN</span>
              <span className="cfc-gate-box"><BookOpen size={22} /></span>
              <span className="cfc-tag">READ IN FULL</span>
              <ArrowRight size={22} className="cfc-gate-arrow" />
            </div>

            {/* The AI panel */}
            <div className="cfc-box cfc-box--panel cfc-fx" style={D(2.0)}>
              <h2>The AI panel</h2>
              <div className="cfc-seats">
                <div className="cfc-seat"><span className="cfc-dot cfc-dot--1" /> Neutral 1</div>
                <div className="cfc-seat"><span className="cfc-dot cfc-dot--2" /> Neutral 2</div>
                <div className="cfc-seat"><span className="cfc-dot cfc-dot--3" /> Neutral 3</div>
              </div>
              <div className="cfc-judge">
                <Scale size={22} />
                <span>AI Agents consider submission</span>
              </div>
            </div>

            <div className="cfc-arrow cfc-arrow--decides cfc-fx" style={D(2.4)}>
              <span className="cfc-tag">DECIDES</span>
              <ArrowRight size={22} />
            </div>

            {/* Written resolution */}
            <div className="cfc-resolution cfc-fx" style={D(2.8)}>
              <h2>Written resolution</h2>
              <p>the reasoning, and which position prevailed</p>
              <div className="cfc-seal"><FileCheck size={22} /></div>
            </div>
          </div>

          {/* Return loop */}
          <div className="cfc-loop-wrap" style={D(3.4)}>
            <span className="cfc-loop-h" aria-hidden="true" />
            <span className="cfc-loop-l" aria-hidden="true" />
            <span className="cfc-loop-r" aria-hidden="true" />
            <span className="cfc-loop-bracket" aria-hidden="true" />
            <div className="cfc-loop-label">
              A Neutral Decision
            </div>
            <div className="cfc-devices" aria-hidden="true">
              <Smartphone size={18} />
              <Monitor size={18} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
